/**
 * Web Audio API utility for playing pure tones as auditory stimuli.
 *
 * Uses a lazy singleton `AudioContext` which is created on first use.
 * Handles the browser's autoplay policy by resuming a suspended context.
 *
 * @module nback/audio
 */

/** Singleton audio context, created lazily. */
let audioContext: AudioContext | null = null;

/** Array of active oscillator nodes for `stopAll()`. */
const activeOscillators: OscillatorNode[] = [];

/**
 * Returns the shared `AudioContext`, creating it if needed.
 *
 * @returns The singleton `AudioContext`.
 */
function getAudioContext(): AudioContext {
    if (audioContext === null) {
        audioContext = new AudioContext();
    }
    return audioContext;
}

/**
 * Plays a pure sine tone at the given frequency for the specified duration.
 *
 * If the `AudioContext` is in a `suspended` state (due to browser autoplay
 * policy), calling this function will attempt to resume it.  The tone will
 * start immediately (synchronously) but the caller should still handle the
 * promise if they need to know when playback finishes.
 *
 * The tone includes a small fade-in / fade-out (5 ms) to avoid audible clicks.
 *
 * @param frequency - Frequency in Hz (e.g. 440 for A4).
 * @param duration  - Duration in milliseconds.
 * @returns A promise that resolves when the tone has finished playing.
 */
export function playTone(frequency: number, duration: number): Promise<void> {
    const ctx = getAudioContext();

    // Attempt to resume if suspended (autoplay policy)
    if (ctx.state === "suspended") {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        ctx.resume();
    }

    // Create the oscillator chain
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(frequency, ctx.currentTime);

    // Fade-in (5 ms) to avoid click
    const fadeDuration = 0.005;
    gainNode.gain.setValueAtTime(0, ctx.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.5, ctx.currentTime + fadeDuration);
    // Sustain
    gainNode.gain.setValueAtTime(
        0.5,
        ctx.currentTime + duration / 1000 - fadeDuration
    );
    // Fade-out
    gainNode.gain.linearRampToValueAtTime(0, ctx.currentTime + duration / 1000);

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + duration / 1000);

    activeOscillators.push(oscillator);

    // Return a promise that resolves when the tone finishes
    return new Promise((resolve) => {
        oscillator.onended = () => {
            const idx = activeOscillators.indexOf(oscillator);
            if (idx !== -1) activeOscillators.splice(idx, 1);
            resolve();
        };
    });
}

/**
 * Stops all currently playing tones and cleans up.
 * Safe to call even when nothing is playing.
 */
export function stopAll(): void {
    for (const osc of activeOscillators) {
        try {
            osc.stop();
            osc.disconnect();
        } catch {
            // Already stopped — ignore.
        }
    }
    activeOscillators.length = 0;
}
